import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useMediaQuery } from '@react-hook/media-query';
import { mediaQuery } from '@modernhealth/vital-foundation';

import {
    Button,
    BodyTextSmall,
    HeadingText30,
    HeadingText40,
    HeadingText50,
    Icon,
    pxToRem,
} from '@modernhealth/vital-components';

import { DailyPauseStatsHeaderTestID, DailyPauseStatsSubHeaderTestID } from './testIds';
import { calculateDailyPauseStreakCTA } from 'src/modules/scheduledContent/util/calculateDailyPauseStreakCTA';
import { useFlags } from 'launchdarkly-react-client-sdk';

const Container = styled.div<{ smallScreen: boolean }>(({ smallScreen }) => ({
    display: 'flex',
    flexDirection: smallScreen ? 'column' : 'row',
    alignItems: smallScreen ? 'center' : 'flex-start',
    marginBottom: 40,
}));

const Link = styled('a')({
    cursor: 'pointer',
    textDecoration: 'underline',

    '&:hover': {
        textDecoration: 'underline',
    },
});

const SubHeaderWrapper = styled(Button)({
    width: '100%',
    maxWidth: pxToRem(432),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: pxToRem(24),
    padding: 0,
});

interface DailyPauseSubHeaderProps {
    text: string;
    onClick: VoidFunction;
}

export const DailyPauseStatsSubHeader: React.FC<DailyPauseSubHeaderProps> = ({ text, onClick }) => {
    return (
        <SubHeaderWrapper
            data-testid={DailyPauseStatsSubHeaderTestID.Button}
            variant="flat"
            onClick={onClick}
        >
            <HeadingText30 testID={DailyPauseStatsSubHeaderTestID.Text} marginBottom={0}>
                {text}
            </HeadingText30>
            <Icon name="chevron-right" size={20} />
        </SubHeaderWrapper>
    );
};

export interface DailyPauseStatsHeaderProps {
    headerText?: string;
    weekStreak?: number;
    dayStreak?: number;
    onClick: () => void;
}

export const DailyPauseStatsHeader: React.FC<DailyPauseStatsHeaderProps> = ({
    weekStreak = 0,
    dayStreak = 0,
    headerText,
    onClick,
}) => {
    const { t } = useTranslation();
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('sm', true));
    const PagerHeader = smallScreenDetected ? HeadingText40 : HeadingText50;
    const {
        releaseDiginShowDailyPauseCalendarRemindersMobile,
        releaseDiginShowDailyPauseNotificationsMobile,
    } = useFlags();

    const dailyPauseSettingsEnabled =
        releaseDiginShowDailyPauseCalendarRemindersMobile ||
        releaseDiginShowDailyPauseNotificationsMobile;

    return (
        <div>
            <PagerHeader
                testID={DailyPauseStatsHeaderTestID.Header}
                marginBottom={12}
                marginTop={32}
            >
                {headerText}
            </PagerHeader>
            <Container smallScreen={smallScreenDetected}>
                <BodyTextSmall testID={DailyPauseStatsHeaderTestID.Description}>
                    {calculateDailyPauseStreakCTA(dayStreak, weekStreak, t)}
                </BodyTextSmall>{' '}
                {dailyPauseSettingsEnabled && (
                    <BodyTextSmall marginLeft={smallScreenDetected ? 0 : 4} marginTop={0}>
                        <Link data-testid={DailyPauseStatsHeaderTestID.Link} onClick={onClick}>
                            {t('dailyPause:streaks.stats.manage_reminders')}
                        </Link>
                    </BodyTextSmall>
                )}
            </Container>
        </div>
    );
};
